// pausePacing.ts — what happens after completing a step, driven by the
// clinician-marked pause points + chapter load (src/data/pacing.generated). This
// is the ONLY spacing brake: there is no "too much today" cap. Pause/load stay a
// silent signal — never shown to the user as a label.
//
//   core  + marked pause point → a gentle process landing ("I'm done for now")
//   core  + not a pause point  → advance on a tap, no landing
//   intro                      → free flow, no landings
//   skill                      → no in-chapter landings; a forced exit pause on the
//                                final step ("go and do this now") + an if-then plan
//   self-compassion (sc-*)     → excluded; governed by the one-a-day gate
import { PAUSE_POINTS, CHAPTER_LOAD, type Handoff, type ChapterLoad } from '@/data/pacing.generated';
import type { Programme, Step, Module } from '@/types';
import { moduleOfStep } from '@/services/contentProvider';

export type Landing = 'none' | 'pause' | 'skillExit';

export interface LandingDecision {
  kind: Landing;
  handoff?: Handoff;
}

/** Chapter id = the step's module id (e.g. "act1-c05"). */
const chapterIdOf = (step: Step): string => step.module_id;

const lastStepId = (mod: Module): string | undefined =>
  mod.steps.length ? mod.steps[mod.steps.length - 1].id : undefined;

/** The landing to show after completing `step` (see file header). */
export function landingFor(programme: Programme, step: Step): LandingDecision {
  const chapterId = chapterIdOf(step);
  if (chapterId.startsWith('sc-')) return { kind: 'none' }; // self-compassion: own gate
  const load: ChapterLoad = CHAPTER_LOAD[chapterId] ?? 'core';

  if (load === 'intro') return { kind: 'none' };

  if (load === 'skill') {
    const mod = moduleOfStep(programme, step.id);
    return mod && lastStepId(mod) === step.id ? { kind: 'skillExit' } : { kind: 'none' };
  }

  // core
  const pp = PAUSE_POINTS[step.id];
  return pp ? { kind: 'pause', handoff: pp.handoff } : { kind: 'none' };
}
